import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Slider,
  Typography,
  Paper,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  Stop,
  Bookmark,
  Close,
} from '@mui/icons-material';
import { voicesService } from '../services/voices';
import { projectsService } from '../services/projects';
import { AudioPinResponse, AudioPinCreate, AudioPinUpdate } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';

interface AudioPlayerProps {
  projectId: number;
  projectName?: string;
  audioUrl?: string;
  audioBlob?: Blob;
  textContent?: string;
  voiceId?: string;
  showText?: boolean;
  audioRef?: React.RefObject<HTMLAudioElement>;
  onClose?: () => void;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ projectId, projectName, audioUrl, audioBlob, textContent, voiceId, showText = false, audioRef: externalAudioRef, onClose }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const internalAudioRef = useRef<HTMLAudioElement>(null);
  const audioRef = externalAudioRef || internalAudioRef;
  const textRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [pins, setPins] = useState<AudioPinResponse[]>([]);
  
  // Theme colors
  const bgColor = theme === 'dark' ? '#1e293b' : '#ffffff';
  const textColor = theme === 'dark' ? '#f1f5f9' : '#1e293b';
  const borderColor = theme === 'dark' ? '#334155' : '#e2e8f0';
  const primaryColor = theme === 'dark' ? '#60a5fa' : '#2563eb';
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [editPin, setEditPin] = useState<AudioPinResponse | null>(null);
  const [pinNote, setPinNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState<string | null>(textContent || null);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);

  // Create object URL from blob if provided
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (audioBlob) {
      const url = URL.createObjectURL(audioBlob);
      setBlobUrl(url);
      console.log('AudioPlayer: Blob URL created', url);
      return () => {
        URL.revokeObjectURL(url);
        setBlobUrl(null);
      };
    } else {
      setBlobUrl(null);
    }
  }, [audioBlob]);

  const actualAudioUrl = audioUrl || blobUrl;

  // Update text when textContent prop changes
  useEffect(() => {
    if (textContent) {
      setText(textContent);
    }
  }, [textContent]);

  useEffect(() => {
    loadPins();
  }, [projectId]);

  // Load text content if not provided and voiceId is available
  useEffect(() => {
    if (showText && !text && voiceId && projectId) {
      // Load text from API
      projectsService.getScriptReadingText(projectId, voiceId)
        .then(data => {
          setText(data.text);
        })
        .catch(err => {
          console.error('Failed to load text:', err);
        });
    }
  }, [showText, text, voiceId, projectId]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !actualAudioUrl) return;

    const updateTime = () => {
      if (audio.currentTime !== undefined && !isNaN(audio.currentTime)) {
        setCurrentTime(audio.currentTime);
      }
    };
    const updateDuration = () => {
      if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };
    const handleEnded = () => setIsPlaying(false);
    const handlePlay = () => {
      console.log('AudioPlayer: play event');
      setIsPlaying(true);
    };
    const handlePause = () => {
      console.log('AudioPlayer: pause event');
      setIsPlaying(false);
    };
    const handleCanPlay = () => {
      updateDuration();
    };

    // Sync with audio element's current state
    setIsPlaying(!audio.paused);

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('loadeddata', updateDuration);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('playing', handlePlay);

    // Try to load duration immediately
    if (audio.readyState >= 1) {
      updateDuration();
    }

    // Periodic sync check for external changes
    const syncInterval = setInterval(() => {
      if (audio && actualAudioUrl) {
        const wasPlaying = isPlaying;
        const nowPlaying = !audio.paused;
        if (wasPlaying !== nowPlaying) {
          console.log('AudioPlayer: State sync - isPlaying changed from', wasPlaying, 'to', nowPlaying);
          setIsPlaying(nowPlaying);
        }
        if (Math.abs(audio.currentTime - currentTime) > 0.1) {
          setCurrentTime(audio.currentTime);
        }
      }
    }, 100);

    return () => {
      clearInterval(syncInterval);
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('loadeddata', updateDuration);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('playing', handlePlay);
    };
  }, [actualAudioUrl, audioRef]);

  // Calculate current text position based on audio time
  useEffect(() => {
    if (!text || !duration || duration === 0) {
      setCurrentTextIndex(0);
      return;
    }
    
    // Approximate: calculate which part of text is being read
    // This is an approximation since we don't have word-level timing
    const progress = Math.min(currentTime / duration, 1); // Clamp between 0 and 1
    const textLength = text.length;
    const estimatedIndex = Math.floor(textLength * progress);
    setCurrentTextIndex(estimatedIndex);
    
    // Auto-scroll to current position (only when playing, throttle to avoid too many scrolls)
    if (isPlaying && textRef.current && currentTime > 0) {
      const textElement = textRef.current;
      const spanElements = textElement.querySelectorAll('span');
      if (spanElements.length > 0) {
        const targetIndex = Math.min(Math.floor((spanElements.length - 1) * progress), spanElements.length - 1);
        const currentSpan = spanElements[targetIndex];
        if (currentSpan) {
          // Throttle scrolling - only scroll every 0.5 seconds
          const shouldScroll = Math.floor(currentTime * 2) !== Math.floor((currentTime - 0.1) * 2);
          if (shouldScroll) {
            currentSpan.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
    }
  }, [currentTime, duration, text, isPlaying]);

  const loadPins = async () => {
    try {
      const data = await voicesService.getAudioPins(projectId);
      setPins(data);
    } catch (error) {
      console.error('Failed to load pins:', error);
    }
  };

  const handlePlayPause = async () => {
    const audio = audioRef.current;
    if (!audio) {
      console.log('AudioPlayer: Cannot play - no audio element');
      return;
    }

    if (!actualAudioUrl) {
      console.log('AudioPlayer: Cannot play - no audio URL');
      return;
    }

    // Ensure audio is loaded
    if (audio.readyState === 0) {
      audio.load();
      await new Promise((resolve) => {
        audio.addEventListener('canplay', resolve, { once: true });
      });
    }

    console.log('AudioPlayer: handlePlayPause - isPlaying:', isPlaying, 'paused:', audio.paused);
    
    try {
      if (isPlaying || !audio.paused) {
      audio.pause();
        setIsPlaying(false);
    } else {
        await audio.play();
        setIsPlaying(true);
        console.log('AudioPlayer: Play started successfully');
      }
    } catch (error) {
      console.error('AudioPlayer: Error playing/pausing audio:', error);
      setIsPlaying(false);
    }
  };

  const handleStop = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    setIsPlaying(false);
  };

  const handleSeek = (event: Event, newValue: number | number[]) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = newValue as number;
    setCurrentTime(newValue as number);
  };

  const handleJumpToPin = (timestamp: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = timestamp;
    setCurrentTime(timestamp);
    if (!isPlaying) {
      audio.play();
      setIsPlaying(true);
    }
  };

  const handleAddPin = () => {
    setEditPin(null);
    setPinNote('');
    setPinDialogOpen(true);
  };

  const handleEditPin = (pin: AudioPinResponse) => {
    setEditPin(pin);
    setPinNote(pin.note || '');
    setPinDialogOpen(true);
  };

  const handleSavePin = async () => {
    if (!audioRef.current) return;

    setLoading(true);
    try {
      if (editPin) {
        // Update existing pin
        await voicesService.updateAudioPin(projectId, editPin.id, { note: pinNote });
      } else {
        // Create new pin
        await voicesService.createAudioPin(projectId, {
          timestamp: audioRef.current.currentTime,
          note: pinNote,
        });
      }
      setPinDialogOpen(false);
      setPinNote('');
      setEditPin(null);
      await loadPins();
    } catch (error) {
      console.error('Failed to save pin:', error);
      alert('Pin kaydedilemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePin = async (pinId: number) => {
    if (!window.confirm('Bu pini silmek istediğinize emin misiniz?')) return;

    try {
      await voicesService.deleteAudioPin(projectId, pinId);
      await loadPins();
    } catch (error) {
      console.error('Failed to delete pin:', error);
      alert('Pin silinemedi');
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getPinsAtCurrentTime = () => {
    return pins.filter(pin => Math.abs(pin.timestamp - currentTime) < 0.5);
  };

  if (!actualAudioUrl) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">Ses dosyası yüklenmedi</Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <audio 
        ref={audioRef} 
        key={actualAudioUrl} 
        src={actualAudioUrl || undefined} 
        style={{ display: 'none' }} 
        preload="auto"
        onLoadedMetadata={(e) => {
          const audio = e.currentTarget;
          if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
            setDuration(audio.duration);
          }
        }}
        onCanPlay={(e) => {
          const audio = e.currentTarget;
          if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
            setDuration(audio.duration);
          }
        }}
        onError={(e) => {
          console.error('AudioPlayer: Error loading audio', e);
        }}
      />

      <Paper sx={{ p: 0.5, mb: 0, bgcolor: bgColor, border: `1px solid ${borderColor}`, position: 'relative', height: '79px', display: 'flex', flexDirection: 'column' }}>
        {/* Project Name - Top row */}
        {projectName && (
          <Box 
            sx={{ 
              px: 1,
              py: 0.5,
              borderBottom: `1px solid ${borderColor}`,
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8
              }
            }}
            onClick={() => navigate(`/projects/${projectId}`)}
          >
            <Typography 
              variant="caption" 
              sx={{ 
                color: theme === 'dark' ? '#ffffff' : '#000000',
                fontWeight: 600,
                fontSize: '0.75rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block'
              }}
              title={projectName}
            >
              📄 {projectName}
            </Typography>
          </Box>
        )}
        {/* Controls - Bottom row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap', width: '100%', flex: 1 }}>
          <IconButton 
            onClick={handlePlayPause} 
            size="small"
            sx={{ 
              color: theme === 'dark' ? '#60a5fa' : '#2563eb',
              padding: '4px',
              flexShrink: 0,
              '&:hover': {
                bgcolor: theme === 'dark' ? 'rgba(96, 165, 250, 0.1)' : 'rgba(37, 99, 235, 0.1)'
              }
            }}
          >
            {isPlaying ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
          </IconButton>
          <IconButton 
            onClick={handleStop} 
            size="small"
            sx={{ color: textColor, padding: '4px', flexShrink: 0 }}
          >
            <Stop fontSize="small" />
          </IconButton>
          <IconButton 
            onClick={handleAddPin} 
            size="small" 
            title="Pin Ekle"
            sx={{ 
              color: theme === 'dark' ? '#60a5fa' : '#2563eb',
              padding: '4px',
              flexShrink: 0,
              '&:hover': {
                bgcolor: theme === 'dark' ? 'rgba(96, 165, 250, 0.1)' : 'rgba(37, 99, 235, 0.1)'
              }
            }}
          >
            <Bookmark fontSize="small" />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0, px: 2, display: 'flex', alignItems: 'center' }}>
            <Slider
              value={duration > 0 ? currentTime : 0}
              max={duration > 0 ? duration : 100}
              onChange={handleSeek}
              valueLabelDisplay="auto"
              valueLabelFormat={formatTime}
              size="small"
              sx={{
                width: '100%',
                color: theme === 'dark' ? '#60a5fa' : '#2563eb',
                '& .MuiSlider-thumb': {
                  width: 12,
                  height: 12,
                  bgcolor: theme === 'dark' ? '#60a5fa' : '#2563eb',
                },
                '& .MuiSlider-track': {
                  height: 3,
                  bgcolor: theme === 'dark' ? '#60a5fa' : '#2563eb',
                },
                '& .MuiSlider-rail': {
                  height: 3,
                  bgcolor: theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                },
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ minWidth: 60, color: textColor, fontSize: '0.7rem', flexShrink: 0, ml: 1 }}>
            {formatTime(currentTime)} / {formatTime(duration)}
          </Typography>
          {/* Playback Speed Control */}
          <FormControl size="small" sx={{ minWidth: 70, flexShrink: 0, ml: 1 }}>
            <Select
              value={playbackRate}
              onChange={(e) => {
                const newRate = e.target.value as number;
                setPlaybackRate(newRate);
                if (audioRef.current) {
                  audioRef.current.playbackRate = newRate;
                }
              }}
              sx={{
                color: textColor,
                fontSize: '0.7rem',
                height: '28px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: borderColor,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: primaryColor,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: primaryColor,
                },
                '& .MuiSelect-icon': {
                  color: textColor,
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: bgColor,
                    '& .MuiMenuItem-root': {
                      color: textColor,
                      fontSize: '0.7rem',
                      '&:hover': {
                        bgcolor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                      },
                      '&.Mui-selected': {
                        bgcolor: primaryColor + '20',
                        '&:hover': {
                          bgcolor: primaryColor + '30',
                        },
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value={0.5}>0.5x</MenuItem>
              <MenuItem value={0.75}>0.75x</MenuItem>
              <MenuItem value={1}>1x</MenuItem>
              <MenuItem value={1.25}>1.25x</MenuItem>
              <MenuItem value={1.5}>1.5x</MenuItem>
              <MenuItem value={2}>2x</MenuItem>
            </Select>
          </FormControl>
          {/* Close Button - Inline */}
          {onClose && (
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: textColor,
                padding: '4px',
                ml: 0.5,
                flexShrink: 0,
                '&:hover': {
                  bgcolor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'
                }
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Paper>

      {/* Text Display with Highlighting */}
      {showText && text && (
        <Paper sx={{ p: 2, mb: 2, maxHeight: '400px', overflow: 'auto', bgcolor: bgColor, border: `1px solid ${borderColor}` }}>
          <Typography variant="h6" gutterBottom sx={{ color: textColor }}>
            Senaryo Metni
        </Typography>
          <Box
            ref={textRef}
                sx={{
              p: 2,
              bgcolor: theme === 'dark' ? '#0f172a' : '#f8fafc',
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.9rem',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: textColor,
                }}
              >
            {text.split('').map((char, index) => {
              const isCurrent = index >= currentTextIndex - 50 && index <= currentTextIndex + 50;
              const isPast = index < currentTextIndex - 50;
              return (
                <span
                  key={index}
                  style={{
                    backgroundColor: isCurrent 
                      ? (theme === 'dark' ? 'rgba(255, 193, 7, 0.3)' : 'rgba(255, 193, 7, 0.2)')
                      : isPast 
                        ? (theme === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                        : 'transparent',
                    transition: 'background-color 0.3s ease',
                  }}
                >
                  {char}
                </span>
              );
            })}
          </Box>
      </Paper>
      )}

      {/* Pin Dialog */}
      <Dialog 
        open={pinDialogOpen} 
        onClose={() => setPinDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { bgcolor: bgColor, color: textColor } }}
      >
        <DialogTitle sx={{ color: textColor }}>
          {editPin ? 'Pin Düzenle' : 'Yeni Pin Ekle'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: theme === 'dark' ? '#94a3b8' : '#64748b' }}>
            Zaman: {formatTime(editPin?.timestamp || currentTime)}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Not"
            value={pinNote}
            onChange={(e) => setPinNote(e.target.value)}
            placeholder="Bu nokta hakkında not ekleyin..."
            InputLabelProps={{ sx: { color: textColor } }}
            InputProps={{ 
              sx: { 
                color: textColor,
                '& fieldset': { borderColor: borderColor },
                '&:hover fieldset': { borderColor: theme === 'dark' ? '#60a5fa' : '#2563eb' },
                '&.Mui-focused fieldset': { borderColor: theme === 'dark' ? '#60a5fa' : '#2563eb' }
              } 
            }}
          />
        </DialogContent>
        <DialogActions sx={{ bgcolor: bgColor }}>
          <Button onClick={() => setPinDialogOpen(false)} sx={{ color: textColor }}>
            İptal
          </Button>
          <Button 
            onClick={handleSavePin} 
            variant="contained" 
            disabled={loading}
            sx={{ 
              bgcolor: theme === 'dark' ? '#60a5fa' : '#2563eb',
              color: 'white',
              '&:hover': {
                bgcolor: theme === 'dark' ? '#3b82f6' : '#1d4ed8'
              }
            }}
          >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AudioPlayer;

